import React, { useState, useEffect } from "react";
import { ChangePassWrap } from "components/atoms/Setting/Setting.styles";
import { useForm } from "components/molecules/Form";
import Form from "components/molecules/Form/Form";
import Field from "components/molecules/Field";
import Button from "components/molecules/Button";
import profileService from "services/profileService";
import Toast from "components/molecules/Toast";
import { useNavigate } from "react-router-dom";

const ChangePassword = () => {
  const [form] = useForm();
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("");
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [newPasswordValue, setNewPasswordValue] = useState("");
  const [confirmPasswordValue, setConfirmPasswordValue] = useState("");
  const navigate = useNavigate();

  // Calculate password strength
  const calculatePasswordStrength = (password) => {
    if (!password) return "";
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return "Weak";
    if (strength <= 3) return "Medium";
    return "Strong";
  };

  // Check if passwords match
  useEffect(() => {
    if (confirmPasswordValue) {
      setPasswordsMatch(newPasswordValue === confirmPasswordValue);
    } else {
      setPasswordsMatch(true);
    }
  }, [newPasswordValue, confirmPasswordValue]);

  // Update password strength when new password changes
  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(newPasswordValue));
  }, [newPasswordValue]);

  const handleSubmit = async (data) => {
    try {
      // Client-side validation
      const currentPassword = data['current-password'];
      const newPassword = data['new-password'];
      const confirmPassword = data['confirm-new-password'];

      // Check if all fields are filled
      if (!currentPassword || !newPassword || !confirmPassword) {
        Toast({
          type: 'error',
          message: 'All fields are required',
        });
        return;
      }

      // Check password length
      if (newPassword.length < 8) {
        Toast({
          type: 'error',
          message: 'New password must be at least 8 characters long',
        });
        return;
      }

      // Check if passwords match
      if (newPassword !== confirmPassword) {
        Toast({
          type: 'error',
          message: 'New password and confirm password do not match',
        });
        return;
      }

      // Check if new password is different from current
      if (currentPassword === newPassword) {
        Toast({
          type: 'error',
          message: 'New password must be different from current password',
        });
        return;
      }

      setLoading(true);

      // Prepare payload
      const payload = {
        currentPassword,
        newPassword,
        confirmPassword,
      };

      // Call API
      const response = await profileService.changePassword(payload);

      if (response.success) {
        Toast({
          type: 'success',
          message: 'Password changed successfully!',
        });

        // Clear form and reset validation
        form.resetFields();
        setNewPasswordValue('');
        setConfirmPasswordValue('');
        setPasswordStrength('');

        // Optional: Logout user after successful password change for security
        // Uncomment the following lines if you want to logout the user
        // setTimeout(() => {
        //   Toast({
        //     type: 'info',
        //     message: 'Please login with your new password',
        //   });
        //   navigate('/login');
        // }, 2000);
      }
    } catch (error) {
      console.error('Error changing password:', error);

      if (error.message === 'UNAUTHORIZED') {
        Toast({
          type: 'error',
          message: 'Session expired. Please login again.',
        });
        navigate('/login');
      } else {
        Toast({
          type: 'error',
          message: error.message || 'Failed to change password',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case "Weak":
        return "#ff4d4f";
      case "Medium":
        return "#faad14";
      case "Strong":
        return "#52c41a";
      default:
        return "#d9d9d9";
    }
  };

  return (
    <ChangePassWrap>
      <div className="heading">
        <h2>Change Password</h2>
        <p>
          Update your password to keep your account secure. Make sure your new password is at least 8 characters long.
        </p>
      </div>
      <Form form={form} onSubmit={handleSubmit}>
        <Form.Item
          label="Enter Current Password *"
          type="password"
          rounded
          name="current-password"
          lg
          placeholder="***********"
          $bgWhite
          rules={[
            {
              required: true,
              message: "Current password is required",
            },
          ]}
        >
          <Field disabled={loading} />
        </Form.Item>
        <Form.Item
          label="Enter New Password *"
          type="password"
          rounded
          name="new-password"
          lg
          placeholder="***********"
          $bgWhite
          rules={[
            {
              required: true,
              message: "New password is required",
            },
            {
              validator: (value) => {
                if (value && value.length < 8) {
                  return "Password must be at least 8 characters long";
                }
                return "";
              },
            },
          ]}
        >
          <Field 
            disabled={loading}
            onChange={(e) => setNewPasswordValue(e.target.value)}
          />
        </Form.Item>
        {newPasswordValue && (
          <div style={{ 
            marginTop: '-12px', 
            marginBottom: '16px',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ color: '#666' }}>Password Strength:</span>
            <span style={{ 
              fontWeight: '600',
              color: getPasswordStrengthColor()
            }}>
              {passwordStrength}
            </span>
            <div style={{
              flex: 1,
              height: '4px',
              backgroundColor: '#f0f0f0',
              borderRadius: '2px',
              overflow: 'hidden',
              maxWidth: '150px'
            }}>
              <div style={{
                height: '100%',
                backgroundColor: getPasswordStrengthColor(),
                width: passwordStrength === 'Weak' ? '33%' : passwordStrength === 'Medium' ? '66%' : '100%',
                transition: 'all 0.3s ease'
              }} />
            </div>
          </div>
        )}
        <Form.Item
          label="Confirm New Password *"
          type="password"
          rounded
          name="confirm-new-password"
          lg
          placeholder="***********"
          $bgWhite
          rules={[
            {
              required: true,
              message: "Confirm new password is required",
            },
          ]}
        >
          <Field 
            disabled={loading}
            onChange={(e) => setConfirmPasswordValue(e.target.value)}
          />
        </Form.Item>
        {confirmPasswordValue && !passwordsMatch && (
          <div style={{ 
            marginTop: '-12px', 
            marginBottom: '16px',
            fontSize: '13px',
            color: '#ff4d4f'
          }}>
            Passwords do not match
          </div>
        )}
        <div className="btn-holder">
          <Button lg disabled={loading || (confirmPasswordValue && !passwordsMatch)}>
            {loading ? 'Updating...' : 'Update Password!'}
          </Button>
        </div>
      </Form>
    </ChangePassWrap>
  );
};

export default ChangePassword;
